import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Calendar, Fish, ExternalLink, Trash2, Pencil, ChevronDown, ChevronUp, Award, Scale } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { TripDialog } from "@/components/trips/TripDialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CatchDetailModal } from "@/components/trips/CatchDetailModal";

interface Catch {
  id: string;
  fish_species: string;
  weight_lbs: number;
  date_caught: string;
  general_location: string;
  photo_url: string | null;
  status: string;
  video_url?: string;
  length_inches?: number;
  girth_inches?: number;
  angler_notes?: string;
}

interface Trip {
  id: string;
  trip_name: string;
  trip_date: string;
  notes: string | null;
  created_at: string;
  user_id: string;
  is_public: boolean;
  ticket_link: string | null;
  catches?: Catch[];
}

const Trips = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState<Trip | null>(null);
  const [expandedTrips, setExpandedTrips] = useState<Set<string>>(new Set());
  const [selectedCatch, setSelectedCatch] = useState<Catch | null>(null);
  const [selectedTripCatches, setSelectedTripCatches] = useState<Catch[]>([]);

  useEffect(() => {
    if (!session) {
      navigate("/auth");
      return;
    }
    fetchTrips();
  }, [session, navigate]);

  const fetchTrips = async () => {
    try {
      const { data, error } = await supabase
        .from("trips")
        .select(`
          *,
          catches:catches(
            id,
            fish_species,
            weight_lbs,
            date_caught,
            general_location,
            photo_url,
            status,
            video_url,
            length_inches,
            girth_inches,
            angler_notes
          )
        `)
        .order("trip_date", { ascending: false });

      if (error) throw error;
      setTrips((data as any) || []);
    } catch (error: any) {
      toast.error("Failed to load trips");
      console.error("Error fetching trips:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTripExpanded = (tripId: string) => {
    setExpandedTrips(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tripId)) {
        newSet.delete(tripId);
      } else {
        newSet.add(tripId);
      }
      return newSet;
    });
  };

  const handleCatchClick = (catchItem: Catch, tripCatches: Catch[]) => {
    setSelectedCatch(catchItem);
    setSelectedTripCatches(tripCatches);
  };

  const handleNextCatch = () => {
    if (!selectedCatch) return;
    const currentIndex = selectedTripCatches.findIndex(c => c.id === selectedCatch.id);
    if (currentIndex < selectedTripCatches.length - 1) {
      setSelectedCatch(selectedTripCatches[currentIndex + 1]);
    }
  };

  const handlePreviousCatch = () => {
    if (!selectedCatch) return;
    const currentIndex = selectedTripCatches.findIndex(c => c.id === selectedCatch.id);
    if (currentIndex > 0) {
      setSelectedCatch(selectedTripCatches[currentIndex - 1]);
    }
  };

  const currentCatchIndex = selectedCatch 
    ? selectedTripCatches.findIndex(c => c.id === selectedCatch.id)
    : -1;

  const handleDelete = async (tripId: string) => {
    if (!confirm("Are you sure you want to delete this trip? Catches will not be deleted.")) {
      return;
    }

    try {
      const { error } = await supabase
        .from("trips")
        .delete()
        .eq("id", tripId);

      if (error) throw error;
      toast.success("Trip deleted successfully");
      fetchTrips();
    } catch (error: any) {
      toast.error("Failed to delete trip");
      console.error("Error deleting trip:", error);
    }
  };

  const handleEdit = (trip: Trip) => {
    setEditingTrip(trip);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingTrip(null);
    fetchTrips();
  };

  if (!session) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main id="main-content" className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-foreground">My Fishing Trips</h1>
          <Button onClick={() => setIsDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Trip
          </Button>
        </div>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-6 w-40" />
                    <div className="flex gap-2">
                      <Skeleton className="h-8 w-8" />
                      <Skeleton className="h-8 w-8" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-16 w-full" />
                  <div className="grid grid-cols-3 gap-2 p-3">
                    {[1, 2, 3].map((j) => (
                      <div key={j} className="flex flex-col items-center space-y-2">
                        <Skeleton className="h-4 w-4" />
                        <Skeleton className="h-3 w-16" />
                        <Skeleton className="h-6 w-12" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : trips.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground mb-4">No fishing trips yet</p>
              <Button onClick={() => setIsDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Trip
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {trips.map((trip) => {
              const isExpanded = expandedTrips.has(trip.id);
              const catchCount = trip.catches?.length || 0;
              const biggestFish = trip.catches?.reduce((max, c) => 
                c.weight_lbs > (max?.weight_lbs || 0) ? c : max, 
                trip.catches[0]
              );
              const totalWeight = trip.catches?.reduce((sum, c) => sum + Number(c.weight_lbs), 0) || 0;
              
              return (
                <Card key={trip.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="truncate">{trip.trip_name}</span>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(trip)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(trip.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4 mr-2" />
                        {format(new Date(trip.trip_date), "PPP")}
                      </div>
                      {trip.notes && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                          {trip.notes}
                        </p>
                      )}
                      
                      {catchCount > 0 && (
                        <div className="grid grid-cols-3 gap-2 mt-4 p-3 bg-accent/30 rounded-lg">
                          <div className="flex flex-col items-center">
                            <Fish className="w-4 h-4 text-muted-foreground mb-1" />
                            <p className="text-xs text-muted-foreground">Total Catches</p>
                            <p className="text-lg font-semibold">{catchCount}</p>
                          </div>
                          <div className="flex flex-col items-center">
                            <Award className="w-4 h-4 text-muted-foreground mb-1" />
                            <p className="text-xs text-muted-foreground">Biggest</p>
                            <p className="text-lg font-semibold">{biggestFish?.weight_lbs || 0} lbs</p>
                          </div>
                          <div className="flex flex-col items-center">
                            <Scale className="w-4 h-4 text-muted-foreground mb-1" />
                            <p className="text-xs text-muted-foreground">Total Weight</p>
                            <p className="text-lg font-semibold">{totalWeight.toFixed(1)} lbs</p>
                          </div>
                        </div>
                      )}
                      
                      {trip.ticket_link && (
                        <a
                          href={trip.ticket_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 text-sm text-primary hover:underline mt-3 p-2 bg-primary/10 rounded-lg"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Buy Trip Tickets
                        </a>
                      )}
                      
                      <Button
                        onClick={() => navigate(`/add-catch?tripId=${trip.id}`)}
                        variant="outline"
                        className="w-full mt-3"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add your catch to this trip
                      </Button>
                      
                      {catchCount > 0 && (
                        <Collapsible open={isExpanded} onOpenChange={() => toggleTripExpanded(trip.id)}>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" className="w-full justify-between p-2 h-auto mt-2">
                              <div className="flex items-center text-sm">
                                <Fish className="w-4 h-4 mr-2" />
                                {catchCount} {catchCount === 1 ? 'catch' : 'catches'}
                              </div>
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-2 space-y-2">
                            {trip.catches?.map((catchItem) => (
                              <div
                                key={catchItem.id}
                                className="border rounded-lg p-3 hover:bg-accent/50 transition-colors cursor-pointer"
                                onClick={() => handleCatchClick(catchItem, trip.catches || [])}
                              >
                                <div className="flex items-start gap-3">
                                  {catchItem.photo_url && (
                                    <img
                                      src={catchItem.photo_url}
                                      alt={catchItem.fish_species}
                                      className="w-16 h-16 object-cover rounded"
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">{catchItem.fish_species}</p>
                                    <p className="text-sm text-muted-foreground">{catchItem.weight_lbs} lbs</p>
                                    <p className="text-xs text-muted-foreground truncate">{catchItem.general_location}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                      
                      {catchCount === 0 && (
                        <div className="flex items-center text-sm text-muted-foreground mt-3">
                          <Fish className="w-4 h-4 mr-2" />
                          No catches yet
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <TripDialog
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
          trip={editingTrip}
        />

        <CatchDetailModal
          catch={selectedCatch}
          isOpen={!!selectedCatch}
          onClose={() => setSelectedCatch(null)}
          onNext={handleNextCatch}
          onPrevious={handlePreviousCatch}
          hasNext={currentCatchIndex < selectedTripCatches.length - 1}
          hasPrevious={currentCatchIndex > 0}
        />
      </main>
    </div>
  );
};

export default Trips;
